import React, { useEffect, useMemo, useState } from "react";
import {
    Badge,
    Box,
    Button,
    Divider,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Spinner,
    Switch,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { CheckCircle2, RefreshCw, ShieldCheck, WalletCards } from "lucide-react";
import { financeApi } from "./financeApi";
import FinancePageShell from "./common/FinancePageShell";
import FinanceSectionCard from "./common/FinanceSectionCard";
import FinanceStatCard from "./common/FinanceStatCard";
import { formatDateTime, formatMoney } from "./common/formatters";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

const getStatusScheme = (status?: string) => {
    if (status === "paid") return "green";
    if (status === "failed") return "red";
    if (status === "pending") return "orange";
    if (status === "processing") return "blue";
    return "gray";
};

const AdminPayoutBatchDetailsPage: React.FC = () => {
    const toast = useToast();
    const { theme } = useCustomTheme();
    const { batchId = "" } = useParams();

    const itemPaidModal = useDisclosure();
    const batchPaidModal = useDisclosure();

    const [loading, setLoading] = useState(true);
    const [marking, setMarking] = useState<string | null>(null);
    const [payingBatch, setPayingBatch] = useState(false);
    const [payload, setPayload] = useState<any>(null);
    const [selectedItem, setSelectedItem] = useState<any>(null);
    const [transferReference, setTransferReference] = useState("");
    const [batchTransferReference, setBatchTransferReference] = useState("");
    const [showIndividualControls, setShowIndividualControls] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const res = await financeApi.getPayoutBatchById(batchId);
            setPayload(res?.data || null);
        } catch (error: any) {
            toast({
                status: "error",
                title: "Failed to load batch details",
                description:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Please try again.",
            });
        } finally {
            setLoading(false);
        }
    };

    const openMarkPaidModal = (row: any) => {
        setSelectedItem(row);
        setTransferReference(row.transferReference || "");
        itemPaidModal.onOpen();
    };

    const closeMarkPaidModal = () => {
        if (marking) return;
        setSelectedItem(null);
        setTransferReference("");
        itemPaidModal.onClose();
    };

    const openBatchPaidModal = () => {
        setBatchTransferReference("");
        batchPaidModal.onOpen();
    };

    const closeBatchPaidModal = () => {
        if (payingBatch) return;
        setBatchTransferReference("");
        batchPaidModal.onClose();
    };

    const markPaid = async () => {
        if (!selectedItem) return;

        const reference = transferReference.trim();

        if (!reference) {
            toast({
                status: "warning",
                title: "Transfer reference required",
                description:
                    "Add the bank, mobile money, or provider reference before marking this payout paid.",
            });
            return;
        }

        try {
            setMarking(selectedItem._id);

            await financeApi.markPayoutItemPaid(selectedItem._id, {
                payoutType: selectedItem.beneficiaryType || "seller",
                transferReference: reference,
            });

            toast({
                status: "success",
                title: "Payout item marked paid",
                description:
                    "Only this item was marked paid. The batch amount has been refreshed.",
            });

            closeMarkPaidModal();
            await load();
        } catch (error: any) {
            toast({
                status: "error",
                title: "Failed to mark payout item paid",
                description:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Please try again.",
            });
        } finally {
            setMarking(null);
        }
    };

    const markWholeBatchPaid = async () => {
        const reference = batchTransferReference.trim();

        if (!reference) {
            toast({
                status: "warning",
                title: "Transfer reference required",
                description:
                    "Add the bank, mobile money, or provider reference for this batch payment.",
            });
            return;
        }

        try {
            setPayingBatch(true);

            await financeApi.markPayoutBatchPaid(batchId, {
                transferReference: reference,
            });

            toast({
                status: "success",
                title: "Batch marked paid",
                description:
                    "All pending payout items in this batch were marked paid.",
            });

            closeBatchPaidModal();
            await load();
        } catch (error: any) {
            toast({
                status: "error",
                title: "Failed to mark batch paid",
                description:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Please try again.",
            });
        } finally {
            setPayingBatch(false);
        }
    };

    useEffect(() => {
        if (batchId) {
            load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batchId]);

    const batch = payload?.batch;
    const items = payload?.items || [];
    const allocations = payload?.allocations || payload?.sellerAllocations || [];
    const bookingSettlements = payload?.bookingSettlements || [];
    const driverEarnings = payload?.driverEarnings || [];

    const summary = useMemo(() => {
        return items.reduce(
            (acc: any, item: any) => {
                const amount = Number(item.amount || 0);

                acc.itemCount += 1;
                acc.total += amount;

                if (item.status === "paid") {
                    acc.paidCount += 1;
                    acc.paidAmount += amount;
                    return acc;
                }

                if (item.status === "failed") {
                    acc.failedCount += 1;
                    acc.failedAmount += amount;
                    return acc;
                }

                acc.pendingCount += 1;
                acc.pendingAmount += amount;

                return acc;
            },
            {
                itemCount: 0,
                total: 0,
                paidCount: 0,
                paidAmount: 0,
                pendingCount: 0,
                pendingAmount: 0,
                failedCount: 0,
                failedAmount: 0,
            },
        );
    }, [items]);

    const canPayWholeBatch =
        !!batch &&
        batch.status !== "paid" &&
        summary.pendingCount > 0 &&
        summary.pendingAmount > 0;

    if (loading) {
        return (
            <FinancePageShell title="Payout Batch Details">
                <VStack py={10}>
                    <Spinner />
                    <Text color={theme.textMuted}>
                        Loading payout batch details...
                    </Text>
                </VStack>
            </FinancePageShell>
        );
    }

    if (!batch) {
        return (
            <FinancePageShell title="Payout Batch Details">
                <FinanceSectionCard title="Batch not found">
                    <Text color={theme.textMuted}>
                        The payout batch could not be found.
                    </Text>
                </FinanceSectionCard>
            </FinancePageShell>
        );
    }

    return (
        <FinancePageShell
            title="Payout Batch Details"
            subtitle="Use this page for individual batch control. For the easiest flow, use Pay All from the accumulated payout list."
        >
            <SimpleGrid columns={{ base: 1, md: 2, xl: 5 }} spacing={4}>
                <FinanceStatCard
                    label="Batch Outstanding"
                    value={formatMoney(batch.totalAmount)}
                />
                <FinanceStatCard label="Items" value={String(summary.itemCount)} />
                <FinanceStatCard label="Paid" value={formatMoney(summary.paidAmount)} />
                <FinanceStatCard
                    label="Still Pending"
                    value={formatMoney(summary.pendingAmount)}
                />
                <FinanceStatCard label="Status" value={String(batch.status || "draft")} />
            </SimpleGrid>

            <FinanceSectionCard
                title="Batch payment"
                rightSlot={
                    <Button
                        leftIcon={<RefreshCw size={16} />}
                        size="sm"
                        variant="outline"
                        onClick={load}
                    >
                        Refresh
                    </Button>
                }
            >
                <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4} mb={5}>
                    <Box>
                        <Text
                            fontSize="xs"
                            color={theme.textMuted}
                            fontWeight="700"
                        >
                            Batch ID
                        </Text>
                        <Text
                            color={theme.text}
                            fontWeight="700"
                            wordBreak="break-all"
                        >
                            {batch._id}
                        </Text>
                    </Box>

                    <Box>
                        <Text
                            fontSize="xs"
                            color={theme.textMuted}
                            fontWeight="700"
                        >
                            Type
                        </Text>
                        <Badge
                            colorScheme={
                                batch.payoutType === "driver" ? "purple" : "teal"
                            }
                        >
                            {batch.payoutType || "seller"}
                        </Badge>
                    </Box>

                    <Box>
                        <Text
                            fontSize="xs"
                            color={theme.textMuted}
                            fontWeight="700"
                        >
                            Period
                        </Text>
                        <Text color={theme.text}>{batch.periodType || "weekly"}</Text>
                    </Box>

                    <Box>
                        <Text
                            fontSize="xs"
                            color={theme.textMuted}
                            fontWeight="700"
                        >
                            Created
                        </Text>
                        <Text color={theme.text}>{formatDateTime(batch.createdAt)}</Text>
                    </Box>
                </SimpleGrid>

                <Box
                    p={4}
                    borderRadius="16px"
                    border={`1px solid ${theme.border}`}
                    bg={theme.surfaceAlt}
                >
                    <HStack align="start" spacing={3}>
                        <ShieldCheck size={18} color={theme.primary} />
                        <Box flex="1">
                            <Text color={theme.text} fontWeight="800">
                                Pay this single batch
                            </Text>
                            <Text color={theme.textMuted} fontSize="sm" mt={1}>
                                This pays all pending payout items inside this one batch only.
                                To pay the same seller or driver across multiple batches, use the
                                accumulated Pay All section on the previous page.
                            </Text>
                        </Box>
                    </HStack>

                    <HStack mt={4} spacing={3} flexWrap="wrap">
                        <Button
                            leftIcon={<WalletCards size={16} />}
                            onClick={openBatchPaidModal}
                            isDisabled={!canPayWholeBatch}
                            color={theme.textOnPrimary}
                            bgImage={theme.gradients.primaryButton}
                            _hover={{
                                bgImage: theme.gradients.primaryButtonHover,
                            }}
                        >
                            Mark This Batch Paid
                        </Button>

                        {!canPayWholeBatch && (
                            <Text color={theme.textMuted} fontSize="sm">
                                This batch has no pending payable items.
                            </Text>
                        )}
                    </HStack>
                </Box>
            </FinanceSectionCard>

            <FinanceSectionCard
                title="Individual payout items"
                rightSlot={
                    <HStack>
                        <Text
                            color={theme.textMuted}
                            fontSize="sm"
                            fontWeight="700"
                        >
                            Show item controls
                        </Text>
                        <Switch
                            isChecked={showIndividualControls}
                            onChange={(e) =>
                                setShowIndividualControls(e.target.checked)
                            }
                        />
                    </HStack>
                }
            >
                <Box
                    mb={4}
                    p={3}
                    borderRadius="14px"
                    border={`1px solid ${theme.border}`}
                    bg={theme.surfaceAlt}
                >
                    <HStack align="start" spacing={3}>
                        <ShieldCheck size={18} color={theme.primary} />
                        <Text color={theme.textMuted} fontSize="sm">
                            Keep this off for normal use. Turn it on only when you need to record a partial transfer for one seller or driver inside this batch.
                        </Text>
                    </HStack>
                </Box>

                <Box overflowX="auto">
                    <Table size="sm">
                        <Thead>
                            <Tr>
                                <Th>Beneficiary</Th>
                                <Th>Recipient</Th>
                                <Th isNumeric>Amount</Th>
                                <Th>Status</Th>
                                <Th>Transfer Ref</Th>
                                <Th>Created</Th>
                                <Th />
                            </Tr>
                        </Thead>
                        <Tbody>
                            {items.map((row: any) => (
                                <Tr key={row._id}>
                                    <Td>
                                        <Badge
                                            colorScheme={
                                                row.beneficiaryType === "driver"
                                                    ? "purple"
                                                    : "teal"
                                            }
                                        >
                                            {row.beneficiaryType || "seller"}
                                        </Badge>
                                    </Td>

                                    <Td>
                                        <Text color={theme.text} fontWeight="700">
                                            {row.sellerUserId || row.driverId || "—"}
                                        </Text>
                                        <Text color={theme.textMuted} fontSize="xs">
                                            {row._id}
                                        </Text>
                                    </Td>

                                    <Td isNumeric fontWeight="700">
                                        {formatMoney(row.amount, row.currency)}
                                    </Td>

                                    <Td>
                                        <Badge colorScheme={getStatusScheme(row.status)}>
                                            {row.status || "pending"}
                                        </Badge>
                                    </Td>

                                    <Td>{row.transferReference || "—"}</Td>

                                    <Td>{formatDateTime(row.createdAt)}</Td>

                                    <Td textAlign="right">
                                        {row.status === "paid" ? (
                                            <Badge colorScheme="green">Done</Badge>
                                        ) : showIndividualControls ? (
                                            <Tooltip
                                                hasArrow
                                                label="Use only when you paid this one seller or driver separately."
                                            >
                                                <Button
                                                    size="sm"
                                                    leftIcon={<CheckCircle2 size={15} />}
                                                    onClick={() => openMarkPaidModal(row)}
                                                    isDisabled={
                                                        row.status === "failed" ||
                                                        Number(row.amount || 0) <= 0
                                                    }
                                                >
                                                    Mark Item Paid
                                                </Button>
                                            </Tooltip>
                                        ) : (
                                            <Text color={theme.textMuted} fontSize="xs">
                                                Hidden
                                            </Text>
                                        )}
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>

                    {!items.length && (
                        <Text mt={4} color={theme.textMuted}>
                            No payout items found in this batch.
                        </Text>
                    )}
                </Box>
            </FinanceSectionCard>

            <FinanceSectionCard title="Included records snapshot">
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
                    <FinanceStatCard
                        label="Product allocations"
                        value={String(allocations.length)}
                    />
                    <FinanceStatCard
                        label="Booking settlements"
                        value={String(bookingSettlements.length)}
                    />
                    <FinanceStatCard
                        label="Driver earnings"
                        value={String(driverEarnings.length)}
                    />
                </SimpleGrid>

                <Divider mb={4} borderColor={theme.border} />

                <Box overflowX="auto">
                    <Table size="sm">
                        <Thead>
                            <Tr>
                                <Th>Source</Th>
                                <Th>Owner</Th>
                                <Th>Reference</Th>
                                <Th>Status</Th>
                                <Th isNumeric>Amount</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {allocations.map((row: any) => (
                                <Tr key={`allocation-${row._id}`}>
                                    <Td>Product allocation</Td>
                                    <Td>{row.sellerUserId || "—"}</Td>
                                    <Td>{row.orderId || row.orderItemId || "—"}</Td>
                                    <Td>
                                        <Badge
                                            colorScheme={getStatusScheme(
                                                row.settlementStatus,
                                            )}
                                        >
                                            {row.settlementStatus || "pending"}
                                        </Badge>
                                    </Td>
                                    <Td isNumeric>
                                        {formatMoney(
                                            row.netSellerAmount,
                                            row.currency,
                                        )}
                                    </Td>
                                </Tr>
                            ))}

                            {bookingSettlements.map((row: any) => (
                                <Tr key={`booking-${row._id}`}>
                                    <Td>{row.entityType || "Booking"}</Td>
                                    <Td>{row.ownerUserId || "—"}</Td>
                                    <Td>{row.entityId || "—"}</Td>
                                    <Td>
                                        <Badge
                                            colorScheme={getStatusScheme(
                                                row.settlementStatus,
                                            )}
                                        >
                                            {row.settlementStatus || "pending"}
                                        </Badge>
                                    </Td>
                                    <Td isNumeric>
                                        {formatMoney(row.netOwnerAmount, row.currency)}
                                    </Td>
                                </Tr>
                            ))}

                            {driverEarnings.map((row: any) => (
                                <Tr key={`earning-${row._id}`}>
                                    <Td>Driver earning</Td>
                                    <Td>{row.driverId || "—"}</Td>
                                    <Td>
                                        {row.dispatchJobId ||
                                            row.deliveryId ||
                                            row.orderId ||
                                            "—"}
                                    </Td>
                                    <Td>
                                        <Badge colorScheme={getStatusScheme(row.status)}>
                                            {row.status || "pending"}
                                        </Badge>
                                    </Td>
                                    <Td isNumeric>
                                        {formatMoney(row.amount, row.currency)}
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>

                    {!allocations.length &&
                        !bookingSettlements.length &&
                        !driverEarnings.length && (
                            <Text mt={4} color={theme.textMuted}>
                                No included records were returned for this batch.
                            </Text>
                        )}
                </Box>
            </FinanceSectionCard>

            <Modal
                isOpen={batchPaidModal.isOpen}
                onClose={closeBatchPaidModal}
                isCentered
            >
                <ModalOverlay />
                <ModalContent bg={theme.surface} borderRadius="20px">
                    <ModalHeader color={theme.text}>Mark this batch as paid</ModalHeader>
                    <ModalCloseButton isDisabled={payingBatch} />

                    <ModalBody>
                        <VStack align="stretch" spacing={4}>
                            <Box
                                p={4}
                                borderRadius="16px"
                                bg={theme.surfaceAlt}
                                border={`1px solid ${theme.border}`}
                            >
                                <Text
                                    color={theme.textMuted}
                                    fontSize="xs"
                                    fontWeight="700"
                                >
                                    Batch Pending
                                </Text>
                                <Text color={theme.text} fontWeight="800">
                                    {formatMoney(summary.pendingAmount)}
                                </Text>

                                <Text
                                    color={theme.textMuted}
                                    fontSize="xs"
                                    fontWeight="700"
                                    mt={3}
                                >
                                    Pending Items
                                </Text>
                                <Text color={theme.text} fontWeight="700">
                                    {summary.pendingCount}
                                </Text>
                            </Box>

                            <FormControl isRequired>
                                <FormLabel color={theme.text}>
                                    Transfer reference
                                </FormLabel>
                                <Input
                                    value={batchTransferReference}
                                    onChange={(e) =>
                                        setBatchTransferReference(e.target.value)
                                    }
                                    placeholder="Bank / Airtel Money / Mpamba reference"
                                    bg={theme.inputBg}
                                    borderColor={theme.inputBorder}
                                />
                            </FormControl>

                            <Text color={theme.textMuted} fontSize="sm">
                                This pays this batch only. To pay the same beneficiary across many batches, use Pay All on the payout batches page.
                            </Text>
                        </VStack>
                    </ModalBody>

                    <ModalFooter gap={2}>
                        <Button
                            variant="outline"
                            onClick={closeBatchPaidModal}
                            isDisabled={payingBatch}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={markWholeBatchPaid}
                            isLoading={payingBatch}
                            color={theme.textOnPrimary}
                            bgImage={theme.gradients.primaryButton}
                            _hover={{
                                bgImage: theme.gradients.primaryButtonHover,
                            }}
                        >
                            Confirm Batch Paid
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal
                isOpen={itemPaidModal.isOpen}
                onClose={closeMarkPaidModal}
                isCentered
            >
                <ModalOverlay />
                <ModalContent bg={theme.surface} borderRadius="20px">
                    <ModalHeader color={theme.text}>
                        Mark individual payout item as paid
                    </ModalHeader>
                    <ModalCloseButton isDisabled={!!marking} />

                    <ModalBody>
                        <VStack align="stretch" spacing={4}>
                            <Box
                                p={4}
                                borderRadius="16px"
                                bg={theme.surfaceAlt}
                                border={`1px solid ${theme.border}`}
                            >
                                <Text
                                    color={theme.textMuted}
                                    fontSize="xs"
                                    fontWeight="700"
                                >
                                    Beneficiary
                                </Text>
                                <Text color={theme.text} fontWeight="700">
                                    {selectedItem?.sellerUserId ||
                                        selectedItem?.driverId ||
                                        "—"}
                                </Text>

                                <Text
                                    color={theme.textMuted}
                                    fontSize="xs"
                                    fontWeight="700"
                                    mt={3}
                                >
                                    Amount
                                </Text>
                                <Text color={theme.text} fontWeight="800">
                                    {formatMoney(
                                        selectedItem?.amount,
                                        selectedItem?.currency,
                                    )}
                                </Text>
                            </Box>

                            <FormControl isRequired>
                                <FormLabel color={theme.text}>
                                    Transfer reference
                                </FormLabel>
                                <Input
                                    value={transferReference}
                                    onChange={(e) =>
                                        setTransferReference(e.target.value)
                                    }
                                    placeholder="Bank / Airtel Money / Mpamba reference"
                                    bg={theme.inputBg}
                                    borderColor={theme.inputBorder}
                                />
                            </FormControl>

                            <Text color={theme.textMuted} fontSize="sm">
                                This action marks only this payout item as paid.
                            </Text>
                        </VStack>
                    </ModalBody>

                    <ModalFooter gap={2}>
                        <Button
                            variant="outline"
                            onClick={closeMarkPaidModal}
                            isDisabled={!!marking}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={markPaid}
                            isLoading={marking === selectedItem?._id}
                            color={theme.textOnPrimary}
                            bgImage={theme.gradients.primaryButton}
                            _hover={{
                                bgImage: theme.gradients.primaryButtonHover,
                            }}
                        >
                            Confirm Item Paid
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </FinancePageShell>
    );
};

export default AdminPayoutBatchDetailsPage;